// 定义度数转换为弧度的常量
const DEG_TO_RAD = Math.PI / 180;
// 定义弧度转换为度数的常量
const RAD_TO_DEG = 180 / Math.PI;

// 将度数转换为弧度
const toRadians = (degrees) => degrees * DEG_TO_RAD;

// 将弧度转换为度数
const toDegrees = (radians) => radians * RAD_TO_DEG;

// 定义地理坐标类型
export interface GeoCoordinates {
  latitude?: number;
  longitude?: number;
}

/**
 * 计算航向角
 * 计算航向角的公式航向角的计算公式如下：bearing=arctan⁡2(sin⁡(Δ𝜆)⋅cos⁡(𝜙2),cos⁡(𝜙1)⋅sin⁡(𝜙2)−sin⁡(𝜙1)⋅cos⁡(𝜙2)⋅cos⁡(Δ𝜆))
 * 其中：𝜙1和𝜙2分别是起点和终点的纬度（以弧度表示）。𝜆1和𝜆2分别是起点和终点的经度（以弧度表示）。Δ𝜆=𝜆2−𝜆1。
 *          0° (北)
 *          |
 * 315°  \  |  /  45°
 *        \ | /
 *         \|/
 * 270°-----+-----90° (东)
 *         /|\
 *        / | \
 * 225°  /  |  \  135°
 *          |
 *         180° (南)
 * @param start 起点坐标
 * @param end 终点坐标
 * @returns
 */
export function calculateBearing(start, end): number {
  if (!validateCoordinatesEnable(start) || !validateCoordinatesEnable(end)) {
    return 0;
  }
  // 将起点纬度转换为弧度
  const startLatRadians = start.latitude * DEG_TO_RAD;
  // 将终点纬度转换为弧度
  const endLatRadians = end.latitude * DEG_TO_RAD;
  // 计算经度差并转换为弧度
  const deltaLongRadians = (end.longitude - start.longitude) * DEG_TO_RAD;

  // 计算y，用于后续计算方位角
  const y = Math.sin(deltaLongRadians) * Math.cos(endLatRadians);
  // 计算x，用于后续计算方位角
  const x = Math.cos(startLatRadians) * Math.sin(endLatRadians) - Math.sin(startLatRadians) * Math.cos(endLatRadians) * Math.cos(deltaLongRadians);
  // 计算方位角的弧度值
  const bearingRadians = Math.atan2(y, x);

  // 将弧度转换为度数并规范化到0-360度，得到方位角的度数值
  const bearingDegrees = (bearingRadians * (180 / Math.PI) + 360) % 360;

  return bearingDegrees;
}

// 计算多个经纬度坐标的中心点
export function calculateMidCoordinates(points) {
  let xSum = 0;
  let ySum = 0;
  let zSum = 0;

  points.forEach((point) => {
    const φ = toRadians(point.latitude);
    const λ = toRadians(point.longitude);

    xSum += Math.cos(φ) * Math.cos(λ);
    ySum += Math.cos(φ) * Math.sin(λ);
    zSum += Math.sin(φ);
  });

  const totalPoints = points.length;

  const xMid = xSum / totalPoints;
  const yMid = ySum / totalPoints;
  const zMid = zSum / totalPoints;

  const φMid = Math.atan2(zMid, Math.sqrt(xMid * xMid + yMid * yMid));
  const λMid = Math.atan2(yMid, xMid);

  return {
    latitude: toDegrees(φMid),
    longitude: toDegrees(λMid)
  };
}

/**
 * 验证给定的经纬度值是否在有效范围内。
 * @param coordinates - 包含经纬度值的对象。
 * @returns 如果经纬度值有效，返回 true；否则返回 false。
 */
export function validateCoordinatesEnable(coordinates) {
  if (!coordinates) {
    return false;
  }
  const { longitude, latitude } = coordinates || {};
  const lng = safeParseFloat(longitude);
  const lat = safeParseFloat(latitude);
  if (lng === null || lat === null) {
    return false;
  }
  // 经度，范围从 -180 到 180。
  const isLngValid = lng >= -180 && lng <= 180;
  // latitude 纬度，范围从 -90 到 90。
  const isLatValid = lat >= -90 && lat <= 90;
  return isLngValid && isLatValid;
}

/**
 * 安全地解析字符串或数字为浮点数。
 * @param value 要解析的字符串或数字。
 * @returns 解析后的浮点数，如果无法解析则返回 null。
 */
export function safeParseFloat(value): number | null {
  if (typeof value === 'string') {
    const num = parseFloat(value);
    return isNaN(num) ? null : num;
  }
  return typeof value === 'number' ? value : null;
}

/**
 * 判断两个坐标是否相等（精确到小数点后6位）
 * @param a
 * @param b
 * @returns
 */
export function isEqualCoordinates(a, b) {
  if (!validateCoordinatesEnable(a) || !validateCoordinatesEnable(b)) {
    return false;
  }
  const precision = 6;
  const aLat = parseFloat(a.latitude.toFixed(precision));
  const aLon = parseFloat(a.longitude.toFixed(precision));
  const bLat = parseFloat(b.latitude.toFixed(precision));
  const bLon = parseFloat(b.longitude.toFixed(precision));
  return aLat === bLat && aLon === bLon;
}
